import { useAtom, useStore } from "jotai";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import type { GuardianScriptedReply } from "@/api/temp/TrustAIExecutiveService";
import { generateId } from "@/components/AILanding/utils/messageUtils";
import { useSlidePanelMounted } from "@/components/slide-panel";
import { URLs } from "@/configs/menu";
import useViewportWidth from "@/hooks/useViewportWidth";
import loggerClient from "@/libs/LoggerClient";
import { MockScope } from "@/libs/mockScope";
import {
  createGuardianSessionAtom,
  GUARDIAN_CONTENT_MIN_PX,
  GUARDIAN_DOCK_RESIZE_MIN_PX,
  GUARDIAN_MAX_DOCKED_VW,
  GUARDIAN_MIN_DOCKED_PX,
  type GuardianEntry,
  type GuardianRequest,
} from "@/states/guardian";
import {
  estateContext,
  sameGuardianContext,
  type GuardianContext,
} from "./guardianContext";
import GuardianRuntime from "./GuardianRuntime";

export type { GuardianEntry } from "@/states/guardian";

/**
 * One definition for "a question is already in flight for this subject", so
 * the re-entrancy guard in `ask` and the `isThinking` the composer disables
 * itself from can never disagree.
 */
const hasPendingQuestion = (
  entries: GuardianEntry[],
  context: GuardianContext,
) =>
  entries.some(
    item =>
      item.kind === "message" &&
      "thinking" in item.entry &&
      item.entry.thinking &&
      item.request?.kind === "question" &&
      sameGuardianContext(item.context, context),
  );

const pendingEntry = (request: GuardianRequest): GuardianEntry => {
  const id = generateId();
  return {
    kind: "message",
    id,
    context: request.context,
    request,
    entry: { id, role: "assistant", thinking: true, timestamp: Date.now() },
  };
};

const useGuardianController = (sessionKey: string) => {
  const { t } = useTranslation();
  const newSession = () => ({
    key: sessionKey,
    atom: createGuardianSessionAtom(
      estateContext(t("trustAiBoard.guardian.kind.estate")),
    ),
  });
  // Swapping in a fresh atom rather than remounting on a `key` keeps the shell
  // — and the routed page under it — mounted when the authenticated user
  // changes. The previous session's atom is simply dropped, so nothing is
  // cached across tenants the way an `atomFamily` would.
  const [session, setSession] = useState(newSession);
  let active = session;
  if (session.key !== sessionKey) {
    active = newSession();
    setSession(active);
  }
  const [state, setState] = useAtom(active.atom);
  const store = useStore();
  const { pathname, search } = useLocation();
  const pageContexts = useRef(
    new Map<
      symbol,
      { context: GuardianContext; path: string; priority: number }
    >(),
  );
  const currentPath = useRef("");
  currentPath.current = `${pathname}${search}`;
  const registerPageContext = useCallback(
    (context: GuardianContext, priority = 0) => {
      const id = Symbol();
      pageContexts.current.set(id, {
        context,
        path: currentPath.current,
        priority,
      });
      return () => {
        pageContexts.current.delete(id);
      };
    },
    [],
  );
  // The AI landing page is its own assistant surface; Guardian stands down
  // there. One rule, read by the surface and by the shell's space reserve.
  const isHidden = pathname === URLs.aiLanding;
  const viewportWidth = useViewportWidth();
  const minDockedWidthVw = Math.min(
    GUARDIAN_MAX_DOCKED_VW,
    (GUARDIAN_MIN_DOCKED_PX / viewportWidth) * 100,
  );
  const dockedWidthVw = Math.min(
    GUARDIAN_MAX_DOCKED_VW,
    Math.max(minDockedWidthVw, state.dockedWidthVw ?? minDockedWidthVw),
  );
  const canResizeDocked = viewportWidth > GUARDIAN_DOCK_RESIZE_MIN_PX;
  const dockedWidth = canResizeDocked
    ? `${dockedWidthVw}vw`
    : `${GUARDIAN_MIN_DOCKED_PX / 16}rem`;
  // Keep the shell's content minimum and expanded sidebar usable.
  const canReserveDockedSpace =
    viewportWidth * (1 - dockedWidthVw / 100) >= GUARDIAN_CONTENT_MIN_PX;
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const sessionAtom = active.atom;
  const read = useCallback(() => store.get(sessionAtom), [store, sessionAtom]);

  const openContext = useCallback(
    (next?: GuardianContext) => {
      const current = read();
      const page = [...pageContexts.current.values()]
        .filter(item => item.path === currentPath.current)
        .sort((a, b) => b.priority - a.priority)[0]?.context;
      const target = next ?? page ?? current.context;
      const changed = !sameGuardianContext(current.context, target);
      if (
        current.displayMode === "closed" ||
        !document.activeElement?.closest(".tlx-guardian-rail")
      ) {
        returnFocusRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
      }
      if (next)
        loggerClient.recordEvent("Guardian.ContextualAsk", { kind: next.kind });
      if (current.displayMode === "closed")
        loggerClient.recordEvent("Guardian.Opened", {
          mode: current.lastOpenMode,
        });
      if (changed)
        loggerClient.recordEvent("Guardian.ContextChanged", {
          from: current.context.kind,
          to: target.kind,
        });
      const additions: GuardianEntry[] = [];
      if (changed)
        additions.push({
          kind: "context-change",
          id: generateId(),
          from: current.context,
          to: target,
          timestamp: Date.now(),
        });
      if (changed || current.entries.length === 0)
        additions.push(
          pendingEntry({
            kind: "introduction",
            context: target,
            query: "",
            promptId: null,
          }),
        );
      setState({
        ...current,
        displayMode:
          current.displayMode === "closed"
            ? current.lastOpenMode
            : current.displayMode,
        context: target,
        draft: changed ? "" : current.draft,
        recentContexts: [
          target,
          ...current.recentContexts.filter(
            item => !sameGuardianContext(item, target),
          ),
        ].slice(0, 8),
        entries: [...current.entries, ...additions],
        focusVersion: current.focusVersion + 1,
      });
    },
    [read, setState],
  );

  const close = useCallback(() => {
    setState(current => ({ ...current, displayMode: "closed" }));
    loggerClient.recordEvent("Guardian.Closed", {});
    requestAnimationFrame(() => {
      const origin = returnFocusRef.current;
      if (origin?.isConnected && !origin.closest("[inert]")) origin.focus();
      else
        document
          .querySelector<HTMLButtonElement>(
            '[data-test-id="guardian-launcher"]',
          )
          ?.focus();
    });
  }, [setState]);

  const changeMode = useCallback(
    (mode: "floating" | "docked") => {
      setState(current => ({
        ...current,
        displayMode: mode,
        lastOpenMode: mode,
      }));
      loggerClient.recordEvent(
        mode === "docked" ? "Guardian.Docked" : "Guardian.Undocked",
        {},
      );
    },
    [setState],
  );

  const ask = useCallback(
    (prompt: string, promptId: string | null = null) => {
      const query = prompt.trim();
      if (!query) return;
      const current = read();
      // Requests are independently tracked, including introductions in other contexts.
      if (hasPendingQuestion(current.entries, current.context)) return;
      const id = generateId();
      const context = current.context;
      setState({
        ...current,
        draft: "",
        entries: [
          ...current.entries,
          {
            kind: "message",
            id,
            context,
            entry: { id, role: "user", text: query, timestamp: Date.now() },
          },
          pendingEntry({ kind: "question", context, query, promptId }),
        ],
      });
      loggerClient.recordEvent("Guardian.MessageSent", { kind: context.kind });
      if (promptId)
        loggerClient.recordEvent("Guardian.SuggestionClicked", {
          kind: context.kind,
          promptId,
        });
    },
    [read, setState],
  );

  const askInContext = useCallback(
    (
      context: GuardianContext,
      prompt: string,
      promptId: string | null = null,
    ) => {
      openContext(context);
      ask(prompt, promptId);
    },
    [openContext, ask],
  );

  const retry = useCallback(
    (id: string) => {
      setState(current => ({
        ...current,
        entries: current.entries.map(item =>
          item.kind === "message" &&
          item.id === id &&
          item.request &&
          "error" in item.entry &&
          item.entry.error
            ? {
                ...item,
                entry: { ...item.entry, error: undefined, thinking: true },
              }
            : item,
        ),
      }));
    },
    [setState],
  );

  const complete = useCallback(
    (id: string, reply?: GuardianScriptedReply) => {
      setState(current => ({
        ...current,
        entries: current.entries.map(item =>
          item.kind === "message" && item.id === id
            ? {
                ...item,
                reply,
                entry: {
                  id,
                  role: "assistant",
                  timestamp: Date.now(),
                  ...(reply
                    ? {
                        content: [
                          { type: "text" as const, text: reply.markdown },
                        ],
                      }
                    : { error: t("trustAiBoard.guardian.error") }),
                },
              }
            : item,
        ),
      }));
    },
    [setState, t],
  );

  const setDockedWidth = useCallback(
    (width: number) =>
      setState(current => ({
        ...current,
        dockedWidthVw: Math.min(GUARDIAN_MAX_DOCKED_VW, Math.max(0, width)),
      })),
    [setState],
  );
  const setDraft = useCallback(
    (draft: string) => setState(current => ({ ...current, draft })),
    [setState],
  );

  /**
   * Actions are memoised apart from state so the consumers that only dispatch
   * — a row action, a header button — do not re-render on every keystroke,
   * transcript append or resize tick.
   */
  const actions = useMemo(
    () => ({
      registerPageContext,
      open: () => openContext(),
      setContext: openContext,
      close,
      dock: () => changeMode("docked"),
      undock: () => changeMode("floating"),
      ask,
      askInContext,
      setDraft,
      setDockedWidth,
      retry,
      complete,
    }),
    [
      openContext,
      registerPageContext,
      close,
      changeMode,
      ask,
      askInContext,
      setDraft,
      setDockedWidth,
      retry,
      complete,
    ],
  );

  /**
   * The only two values the app shell needs. Kept in its own memo and its own
   * context so the shell — which renders the routed `<Outlet />` — does not
   * re-render on every keystroke, transcript append or resize frame.
   */
  const railMounted = useSlidePanelMounted(state.displayMode !== "closed");
  const reservesLayoutSpace =
    railMounted &&
    (state.displayMode === "closed"
      ? state.lastOpenMode
      : state.displayMode) === "docked" &&
    canReserveDockedSpace &&
    !isHidden;
  const layout = useMemo(
    () => ({ dockedWidth, reservesLayoutSpace }),
    [dockedWidth, reservesLayoutSpace],
  );

  const api = useMemo(
    () => ({
      ...state,
      ...actions,
      dockedWidthVw,
      minDockedWidthVw,
      dockedWidth,
      canResizeDocked,
      canReserveDockedSpace,
      isHidden,
      /** The shell reserves space only for a rail that is actually showing. */
      reservesLayoutSpace,
      isOpen: state.displayMode !== "closed",
      isThinking: hasPendingQuestion(state.entries, state.context),
    }),
    [
      state,
      actions,
      dockedWidthVw,
      minDockedWidthVw,
      dockedWidth,
      canResizeDocked,
      canReserveDockedSpace,
      isHidden,
      reservesLayoutSpace,
    ],
  );

  return { api, actions, layout };
};

type Controller = ReturnType<typeof useGuardianController>;
export type GuardianApi = Controller["api"];
/** Dispatch-only surface: stable for the life of the provider. */
export type GuardianActions = Controller["actions"];
/** Geometry the app shell needs to reserve space for a docked rail. */
export type GuardianLayout = Controller["layout"];

const GuardianCtx = createContext<GuardianApi | null>(null);
const GuardianActionsCtx = createContext<GuardianActions | null>(null);
const GuardianLayoutCtx = createContext<GuardianLayout | null>(null);

export const useOptionalGuardian = () => useContext(GuardianCtx);
export const useGuardian = (): GuardianApi => {
  const guardian = useOptionalGuardian();
  if (!guardian)
    throw new Error("useGuardian must be used within a GuardianProvider");
  return guardian;
};

/**
 * For callers that only dispatch (an "Ask Guardian" row action or header
 * button). Subscribing to this instead of the full api keeps a grid out of
 * Guardian's per-keystroke and per-drag render path. Null outside a provider,
 * which doubles as "Guardian is not available here".
 */
export const useOptionalGuardianActions = () => useContext(GuardianActionsCtx);

/**
 * For the app shell only: the docked width and whether to reserve space for
 * it. Changes at most when the rail is docked, undocked or resized — never on
 * a keystroke — so the routed page under `<Outlet />` stays out of Guardian's
 * render path. Null outside a provider.
 */
export const useOptionalGuardianLayout = () => useContext(GuardianLayoutCtx);

/** The runtime alone opts into mocks; routed children keep their own API scope. */
export const GuardianProvider: React.FC<{
  children: React.ReactNode;
  /** Identifies the authenticated session; a change starts a fresh transcript. */
  sessionKey?: string;
}> = ({ children, sessionKey = "" }) => {
  const { api, actions, layout } = useGuardianController(sessionKey);
  return (
    <GuardianActionsCtx.Provider value={actions}>
      <GuardianLayoutCtx.Provider value={layout}>
        <GuardianCtx.Provider value={api}>
          <MockScope>
            <GuardianRuntime
              entries={api.entries}
              complete={actions.complete}
            />
          </MockScope>
          {children}
        </GuardianCtx.Provider>
      </GuardianLayoutCtx.Provider>
    </GuardianActionsCtx.Provider>
  );
};

/** Registers page facts without changing an open investigation or rerendering the shell. */
export function useGuardianPageContext(
  context: GuardianContext | undefined,
  priority = 0,
) {
  const actions = useOptionalGuardianActions();
  useEffect(
    () =>
      context ? actions?.registerPageContext(context, priority) : undefined,
    [actions, context, priority],
  );
}
